import { createGameStore } from '../lib/createGameStore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CurrencyState = {
  gold: number;
  wood: number;
  ore: number;
  food: number;
  mana: number;
  goldLastTick: number;
  inspiration: {
    amount: number;
    multiplier: number;
    remainingActive: number;
    remainingCooldown: number;
  };
};

export type CurrencyActions = {
  setGold: (gold: number) => void;
  addGold: (amount: number) => void;
  spendGold: (amount: number) => boolean;

  setWood: (wood: number) => void;
  addWood: (amount: number) => void;
  spendWood: (amount: number) => boolean;

  setOre: (ore: number) => void;
  addOre: (amount: number) => void;
  spendOre: (amount: number) => boolean;

  setFood: (food: number) => void;
  addFood: (amount: number) => void;
  spendFood: (amount: number) => boolean;

  setMana: (mana: number) => void;
  consumeMana: (amount: number) => boolean;
  restoreMana: (amount: number, cap: number) => void;

  setGoldLastTick: (amount: number) => void;

  setInspiration: (inspiration: Partial<CurrencyState['inspiration']>) => void;
  activateInspiration: (duration: number, cooldown: number) => void;
  tickInspiration: () => void;

  /** Reset all currency to initial values (e.g. on new game). */
  reset: () => void;
};

export type CurrencyStore = CurrencyState & CurrencyActions;

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialCurrencyState: CurrencyState = {
  gold: 0,
  wood: 0,
  ore: 0,
  food: 0,
  mana: 100,
  goldLastTick: 0,
  inspiration: {
    amount: 0,
    multiplier: 1,
    remainingActive: 0,
    remainingCooldown: 0,
  },
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

/**
 * Tracks all in-game currencies and resources.
 *
 * React usage:
 *   const gold = useCurrencyStore(s => s.gold);
 *   const { addGold } = useCurrencyStore();
 *
 * Excalibur / outside React:
 *   const { gold, mana } = useCurrencyStore.getState();
 *   useCurrencyStore.getState().addGold(50);
 *   useCurrencyStore.subscribe(s => s.gold, g => console.log('gold changed', g));
 */
export const useCurrencyStore = createGameStore<CurrencyStore>(
  { saveKey: 'currency' },
  (set, get) => ({
    ...initialCurrencyState,

    setGold: (gold) => set({ gold }),
    addGold: (amount) => set({ gold: get().gold + amount }),
    spendGold: (amount) => {
      const { gold } = get();
      if (gold < amount) return false;
      set({ gold: gold - amount });
      return true;
    },

    setWood: (wood) => set({ wood }),
    addWood: (amount) => set({ wood: get().wood + amount }),
    spendWood: (amount) => {
      const { wood } = get();
      if (wood < amount) return false;
      set({ wood: wood - amount });
      return true;
    },

    setOre: (ore) => set({ ore }),
    addOre: (amount) => set({ ore: get().ore + amount }),
    spendOre: (amount) => {
      const { ore } = get();
      if (ore < amount) return false;
      set({ ore: ore - amount });
      return true;
    },

    setFood: (food) => set({ food }),
    addFood: (amount) => set({ food: get().food + amount }),
    spendFood: (amount) => {
      const { food } = get();
      if (food < amount) return false;
      set({ food: food - amount });
      return true;
    },

    setMana: (mana) => set({ mana }),
    consumeMana: (amount) => {
      const { mana } = get();
      if (mana < amount) return false;
      set({ mana: mana - amount });
      return true;
    },
    restoreMana: (amount, cap) => {
      set({ mana: Math.min(get().mana + amount, cap) });
    },

    setGoldLastTick: (goldLastTick) => set({ goldLastTick }),

    setInspiration: (partial) =>
      set({ inspiration: { ...get().inspiration, ...partial } }),

    activateInspiration: (duration, cooldown) =>
      set({
        inspiration: {
          ...get().inspiration,
          remainingActive: duration,
          remainingCooldown: cooldown,
        },
      }),

    tickInspiration: () => {
      const { inspiration } = get();
      if (inspiration.remainingActive > 0) {
        set({
          inspiration: {
            ...inspiration,
            remainingActive: inspiration.remainingActive - 1,
          },
        });
      } else if (inspiration.remainingCooldown > 0) {
        set({
          inspiration: {
            ...inspiration,
            remainingCooldown: inspiration.remainingCooldown - 1,
          },
        });
      }
    },

    reset: () => set(initialCurrencyState),
  }),
);
